export type DocumentType =
  | 'GOVERNMENT_ID'
  | 'DIPLOMA'
  | 'BACKGROUND_CHECK'
  | 'IMMUNIZATION'
  | 'CPR_CERT'
  | 'TB_TEST'
  | 'DRUG_SCREENING'
  | 'REFERENCES'
  | 'PROFESSIONAL_REFERENCES'
  | 'NAME_CHANGE_PREVIOUS_LEGAL_NAME'
  | 'NAME_CHANGE_NEW_LEGAL_NAME'
  // New consolidated profile-completion slots
  | 'PHOTO_ID'
  | 'GOVERNMENT_DOCUMENT'
  // Legacy profile-completion types (still appear on historic rows)
  | 'SSN_OR_ITIN'
  | 'PASSPORT_NO_TIN'
  | 'PRIOR_EDUCATION'
  | 'OTHER';

/**
 * Sub-type written on `Document.subType` for the consolidated profile slots.
 * Only present for new uploads under `PHOTO_ID` or `GOVERNMENT_DOCUMENT`.
 */
export type DocumentSubType =
  | 'DRIVERS_LICENSE'
  | 'STATE_ID'
  | 'PASSPORT'
  | 'SSN'
  | 'ITIN';

export const DOCUMENT_SUBTYPE_LABELS: Record<DocumentSubType, string> = {
  DRIVERS_LICENSE: "Driver's license",
  STATE_ID: 'State ID',
  PASSPORT: 'Passport',
  SSN: 'SSN',
  ITIN: 'ITIN',
};

export type DocumentStatus = 'PENDING' | 'UPLOADED' | 'APPROVED' | 'REJECTED';

export interface Document {
  _id: string;
  documentType: DocumentType;
  /** Saved sub-type for PHOTO_ID / GOVERNMENT_DOCUMENT (e.g. DRIVERS_LICENSE). */
  subType?: DocumentSubType | null;
  /** Pre-formatted sub-type label from the API (preferred when present). */
  subTypeLabel?: string | null;
  isRequired: boolean;
  status: DocumentStatus;
  uploadedAt?: string;
  approvedAt?: string;
  approvedBy?: string;
  rejectionReason?: string;
  notes?: string;
  /** Student-authored purpose for `OTHER` uploads. */
  description?: string | null;
  label: string;
  fileId?: string;
  fileUrl?: string;
  pendingFileId?: string | null;
  hasPendingUpdate?: boolean;
  pendingUploadedAt?: string | null;
  updateCount?: number;
  hasBeenUpdated?: boolean;
  student?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

export interface DocumentStats {
  overall: {
    total: number;
    pending: number;
    uploaded: number;
    approved: number;
    rejected: number;
  };
  byType: Array<{
    documentType: DocumentType;
    total: number;
    pending: number;
    uploaded: number;
    approved: number;
    rejected: number;
    label: string;
  }>;
}

export interface DocumentApprovalPayload {
  status: 'APPROVED' | 'REJECTED';
  rejectionReason?: string;
  notes?: string;
  target?: 'document' | 'pending_update';
}

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  GOVERNMENT_ID: 'Government-issued photo ID',
  DIPLOMA: 'High school diploma or GED certificate',
  BACKGROUND_CHECK: 'Background check results',
  IMMUNIZATION: 'Immunization records',
  CPR_CERT: 'CPR certification',
  TB_TEST: 'TB test results',
  DRUG_SCREENING: 'Drug screening results',
  REFERENCES: 'Professional references',
  PROFESSIONAL_REFERENCES: 'Professional references',
  NAME_CHANGE_PREVIOUS_LEGAL_NAME: 'Proof of previous legal name',
  NAME_CHANGE_NEW_LEGAL_NAME: 'Proof of new legal name',
  PHOTO_ID: 'Photo ID',
  GOVERNMENT_DOCUMENT: 'Government document',
  SSN_OR_ITIN: 'SSN or ITIN document',
  PASSPORT_NO_TIN: 'Passport (no SSN/ITIN)',
  PRIOR_EDUCATION: 'Previous education document',
  OTHER: 'Other document',
};

export const DOCUMENT_STATUS_COLORS: Record<DocumentStatus, string> = {
  PENDING: 'bg-gray-100 text-gray-800 border-gray-200',
  UPLOADED: 'bg-blue-100 text-blue-800 border-blue-200',
  APPROVED: 'bg-green-100 text-green-800 border-green-200',
  REJECTED: 'bg-red-100 text-red-800 border-red-200'
};
